
import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

type Role = 'admin' | 'financier' | 'formateur' | 'participant' | 'commercial';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: Role[];
}

export default function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait until the auth state has finished loading
    if (loading) return;

    // If the user is not authenticated, redirect to the login page
    if (!isAuthenticated) {
      navigate('/connexion', { replace: true });
      return;
    }

    // If a role restriction exists and the user role is not permitted,
    // redirect to the appropriate default page for that role
    if (allowedRoles && user && !allowedRoles.includes(user.role as Role)) {
      const redirectMap: Record<Role, string> = {
        admin: '/admin',
        financier: '/admin/finances',
        formateur: '/formateur',
        participant: '/participant',
        commercial: '/', // fallback for commercial or any future role
      };

      const target = redirectMap[user.role as Role] ?? '/';
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, user, allowedRoles, navigate, loading]);

  // Show a loader while the authentication status is being resolved
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#FF6B35]/20 border-t-[#FF6B35] rounded-full animate-spin"></div>
          <p className="text-sm text-gray-500 font-medium">Vérification de la session…</p>
        </div>
      </div>
    );
  }

  // If the user is not allowed to view the route, render nothing (the redirect
  // side‑effect in the useEffect hook will have already taken place)
  if (!isAuthenticated || (allowedRoles && user && !allowedRoles.includes(user.role as Role))) {
    return null;
  }

  // Otherwise render the protected children
  return <>{children}</>;
}
